import { useMemo, useState } from 'react';
import { neutralUnits, starterUnits, tokens } from '../../game/data/cards';
import { TRIBE_DEFINITIONS, getTribeDefinition } from '../../game/data/tribes';
import type { Card } from '../../game/data/cards/types';
import { CardTile } from '../../components/CardTile';
import { useCardDetailStore } from '../../stores/cardDetail';
import { GalleryShell, GallerySection } from '../../components/GalleryShell';

// Every unit in the game, browsable outside a match.
//
// The shop shows five cards for a few seconds at a time, so the only way to
// learn the pool is to lose with it. This is the catalogue: what exists, what
// tier it arrives at, and which tribe it feeds.
//
// Grouping is by TIER rather than tribe by default, because tier is the thing
// that governs when you will actually see a card — a tribe view answers "what
// could this deck become", which is what the tribe filter is for.

type Group = { label: string; sublabel?: string; accent?: string; cards: Card[] };

export function MinionsPage() {
  const [filter, setFilter] = useState('all');
  const openCard = useCardDetailStore((s) => s.open);

  // Tokens are minted by other cards rather than bought, and starters only
  // arrive in the opening draft. Both are real units a player will meet, so
  // they get their own groups instead of being hidden or mixed into the tiers.
  const filters = useMemo(
    () => [
      { id: 'all', label: 'All', hint: String(neutralUnits.length) },
      ...[1, 2, 3, 4, 5, 6].map((t) => ({
        id: `t${t}`,
        label: `Tier ${t}`,
        hint: String(neutralUnits.filter((c) => c.tier === t).length),
      })),
      { id: 'starters', label: 'Starters', hint: String(starterUnits.length) },
      { id: 'tokens', label: 'Tokens', hint: String(tokens.length) },
      ...TRIBE_DEFINITIONS.map((t) => ({
        id: `tribe:${t.id}`,
        label: t.name,
        hint: String(neutralUnits.filter((c) => c.tribe === t.id).length),
      })),
    ],
    [],
  );

  const groups = useMemo<Group[]>(() => {
    const byTier = (cards: Card[]): Group[] =>
      [1, 2, 3, 4, 5, 6]
        .map((t) => ({
          label: `Tier ${t}`,
          sublabel: `Enters the shop from tavern tier ${t}`,
          cards: cards.filter((c) => c.tier === t),
        }))
        .filter((g) => g.cards.length > 0);

    if (filter === 'starters') {
      return [
        {
          label: 'Starters',
          sublabel: 'Hero-specific units, offered only in the opening draft',
          cards: starterUnits,
        },
      ];
    }
    if (filter === 'tokens') {
      return [
        {
          label: 'Tokens',
          sublabel: 'Summoned by other cards — never bought',
          cards: tokens,
        },
      ];
    }
    if (filter.startsWith('tribe:')) {
      const id = filter.slice(6);
      const tribe = getTribeDefinition(id as never);
      return byTier(neutralUnits.filter((c) => c.tribe === id)).map((g) => ({
        ...g,
        accent: tribe ? `#${tribe.color.toString(16).padStart(6, '0')}` : undefined,
        sublabel: tribe ? `${tribe.verb} — ${tribe.fantasy}` : g.sublabel,
      }));
    }
    if (filter.startsWith('t')) {
      const t = Number(filter.slice(1));
      return byTier(neutralUnits).filter((g) => g.label === `Tier ${t}`);
    }
    return byTier(neutralUnits);
  }, [filter]);

  const total = groups.reduce((n, g) => n + g.cards.length, 0);

  return (
    <GalleryShell
      eyebrow="codex"
      title="Minions"
      blurb="Every unit in the pool. Tap a card for its rules, keywords and the tribe it feeds."
      filters={filters}
      activeFilter={filter}
      onFilter={setFilter}
    >
      {total === 0 ? (
        <p className="py-lg text-center font-data text-[12px] uppercase tracking-[0.2em] text-graphite">
          nothing in this filter yet
        </p>
      ) : (
        groups.map((g) => (
          <GallerySection
            key={g.label + (g.accent ?? '')}
            label={g.label}
            sublabel={g.sublabel}
            accent={g.accent}
          >
            <div className="grid grid-cols-2 gap-sm sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {g.cards.map((card) => (
                <CardTile
                  key={card.id}
                  card={card}
                  size="md"
                  onClick={() => openCard(card)}
                />
              ))}
            </div>
          </GallerySection>
        ))
      )}
    </GalleryShell>
  );
}
